<template>
  <div class="min-h-screen bg-gray-50">
    <!-- 页面标题 -->
    <div class="bg-white shadow-sm border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">{{ $store.getters.t('agent_chat') }}</h1>
            <p class="text-sm text-gray-600">{{ $store.getters.t('agent_chat_desc') }}</p>
          </div>
          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-2">
              <div class="w-3 h-3 bg-green-500 rounded-full"></div>
              <span class="text-sm text-gray-600">{{ $store.getters.t('agent_online') }}</span>
            </div>
            <button 
              @click="clearChat"
              class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <i class="fas fa-trash mr-2"></i>
              {{ $store.getters.t('clear_chat') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <!-- 左侧：智能体选择面板 -->
        <div class="lg:col-span-1">
          <div class="bg-white rounded-lg shadow-sm border p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <i class="fas fa-robot mr-2 text-purple-500"></i>
              {{ $store.getters.t('available_agents') }}
            </h3>
            
            <!-- 智能体列表 -->
            <div class="space-y-3">
              <div 
                v-for="agent in availableAgents" 
                :key="agent.id"
                @click="selectAgent(agent)"
                :class="[
                  'p-3 rounded-lg cursor-pointer transition-colors border',
                  selectedAgent?.id === agent.id 
                    ? 'bg-purple-50 border-purple-200' 
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                ]"
              >
                <div class="flex items-center">
                  <div class="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center mr-3">
                    <i :class="agent.icon" class="text-white"></i>
                  </div>
                  <div class="flex-1">
                    <h4 class="font-medium text-gray-900">{{ agent.name }}</h4>
                    <p class="text-sm text-gray-600">{{ agent.description }}</p>
                  </div>
                  <div class="flex items-center">
                    <div class="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span class="text-xs text-gray-500">{{ agent.status }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 快速操作 -->
            <div class="mt-6 pt-4 border-t border-gray-200">
              <h4 class="text-sm font-medium text-gray-900 mb-3">{{ $store.getters.t('quick_actions') }}</h4>
              <div class="space-y-2">
                <button 
                  v-for="action in quickActions"
                  :key="action.id"
                  @click="executeQuickAction(action)"
                  class="w-full text-left p-2 rounded text-sm hover:bg-gray-100 transition-colors"
                >
                  <i :class="action.icon" class="mr-2 text-purple-500"></i>
                  {{ action.name }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧：聊天界面 -->
        <div class="lg:col-span-3">
          <div class="bg-white rounded-lg shadow-sm border h-[600px] flex flex-col">
            <!-- 聊天头部 -->
            <div class="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-500 to-blue-500 text-white">
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center mr-3">
                    <i :class="selectedAgent?.icon || 'fas fa-robot'" class="text-white"></i>
                  </div>
                  <div>
                    <h3 class="font-medium">{{ selectedAgent?.name || $store.getters.t('select_agent') }}</h3>
                    <p class="text-sm opacity-90">{{ selectedAgent?.description || $store.getters.t('select_agent_desc') }}</p>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <div class="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span class="text-sm">{{ $store.getters.t('online') }}</span>
                </div>
              </div>
            </div>

            <!-- 聊天消息区域 -->
            <div class="flex-1 overflow-y-auto p-4 space-y-4" ref="chatContainer">
              <!-- 欢迎消息 -->
              <div v-if="messages.length === 0" class="text-center py-8">
                <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                  <i class="fas fa-robot text-white text-2xl"></i>
                </div>
                <h3 class="text-lg font-medium text-gray-900 mb-2">{{ $store.getters.t('welcome_message') }}</h3>
                <p class="text-gray-600">{{ $store.getters.t('welcome_desc') }}</p>
              </div>

              <!-- 消息列表 -->
              <div 
                v-for="(message, index) in messages" 
                :key="index"
                :class="[
                  'flex',
                  message.type === 'user' ? 'justify-end' : 'justify-start'
                ]"
              >
                <div 
                  :class="[
                    'max-w-xs lg:max-w-md px-4 py-2 rounded-lg',
                    message.type === 'user' 
                      ? 'bg-purple-500 text-white' 
                      : 'bg-gray-100 text-gray-900'
                  ]"
                >
                  <div class="flex items-start">
                    <div v-if="message.type === 'agent'" class="w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center mr-2 flex-shrink-0">
                      <i :class="selectedAgent?.icon || 'fas fa-robot'" class="text-white text-xs"></i>
                    </div>
                    <div class="flex-1">
                      <div class="whitespace-pre-wrap">{{ message.content }}</div>
                      <div class="text-xs opacity-70 mt-1">
                        {{ formatTime(message.timestamp) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 加载状态 -->
              <div v-if="isLoading" class="flex justify-start">
                <div class="bg-gray-100 text-gray-900 max-w-xs lg:max-w-md px-4 py-2 rounded-lg">
                  <div class="flex items-center">
                    <div class="w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center mr-2">
                      <i :class="selectedAgent?.icon || 'fas fa-robot'" class="text-white text-xs"></i>
                    </div>
                    <div class="flex space-x-1">
                      <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
                      <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 输入区域 -->
            <div class="p-4 border-t border-gray-200">
              <div class="flex space-x-3">
                <div class="flex-1">
                  <textarea
                    v-model="inputMessage"
                    @keydown.enter.prevent="sendMessage"
                    :placeholder="$store.getters.t('type_message')"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    rows="2"
                  ></textarea>
                </div>
                <button 
                  @click="sendMessage"
                  :disabled="!inputMessage.trim() || isLoading"
                  class="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  <i class="fas fa-paper-plane mr-2"></i>
                  {{ $store.getters.t('send') }}
                </button>
              </div>
              
              <!-- 快捷回复 -->
              <div class="mt-3 flex flex-wrap gap-2">
                <button 
                  v-for="suggestion in quickSuggestions"
                  :key="suggestion"
                  @click="sendQuickSuggestion(suggestion)"
                  class="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                >
                  {{ suggestion }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgentChatPage',
  data() {
    return {
      selectedAgent: null,
      inputMessage: '',
      messages: [],
      isLoading: false,
      availableAgents: [
        {
          id: 'head-agent',
          name: '总智能体',
          description: '协调和管理所有子智能体',
          icon: 'fas fa-brain',
          status: '在线'
        },
        {
          id: 'streamer-agent',
          name: '主播智能体',
          description: '专门处理主播相关任务',
          icon: 'fas fa-user-tie',
          status: '在线'
        },
        {
          id: 'operation-agent',
          name: '运营智能体',
          description: '处理运营和数据分析任务',
          icon: 'fas fa-chart-line',
          status: '在线'
        },
        {
          id: 'publish-agent',
          name: '发布智能体',
          description: '管理内容发布和推广',
          icon: 'fas fa-share-alt',
          status: '在线'
        }
      ],
      quickActions: [
        {
          id: 'analyze',
          name: '数据分析',
          icon: 'fas fa-chart-bar'
        },
        {
          id: 'schedule',
          name: '任务调度',
          icon: 'fas fa-calendar-alt'
        },
        {
          id: 'optimize',
          name: '性能优化',
          icon: 'fas fa-tachometer-alt'
        },
        {
          id: 'report',
          name: '生成报告',
          icon: 'fas fa-file-alt'
        }
      ],
      quickSuggestions: [
        '请分析今日数据',
        '帮我优化工作流程',
        '生成运营报告',
        '检查系统状态'
      ]
    }
  },
  methods: {
    selectAgent(agent) {
      this.selectedAgent = agent
      this.addSystemMessage(`${agent.name} 已连接，可以开始对话`)
    },
    
    sendMessage() {
      if (!this.inputMessage.trim() || this.isLoading) return
      
      const userMessage = {
        type: 'user',
        content: this.inputMessage,
        timestamp: new Date()
      }
      
      this.messages.push(userMessage)
      this.inputMessage = ''
      this.scrollToBottom()
      
      // 模拟AI响应
      this.isLoading = true
      setTimeout(() => {
        this.generateResponse(userMessage.content)
        this.isLoading = false
      }, 1000 + Math.random() * 2000)
    },
    
    generateResponse(userInput) {
      const responses = [
        `我理解您的需求：${userInput}。让我为您分析并提供解决方案。`,
        `根据您的问题，我建议采取以下措施：\n1. 首先分析当前状况\n2. 制定优化方案\n3. 执行改进措施`,
        `这是一个很好的问题！让我为您详细解释相关流程和最佳实践。`,
        `基于您的输入，我已经启动了相应的处理流程。预计在几分钟内完成。`
      ]
      
      const randomResponse = responses[Math.floor(Math.random() * responses.length)]
      
      const agentMessage = {
        type: 'agent',
        content: randomResponse,
        timestamp: new Date()
      }
      
      this.messages.push(agentMessage)
      this.scrollToBottom()
    },
    
    sendQuickSuggestion(suggestion) {
      this.inputMessage = suggestion
      this.sendMessage()
    },
    
    executeQuickAction(action) {
      const actionMessage = `执行${action.name}操作`
      this.addSystemMessage(actionMessage)
      
      // 模拟执行操作
      this.isLoading = true
      setTimeout(() => {
        this.addSystemMessage(`${action.name}操作已完成`)
        this.isLoading = false
      }, 2000)
    },
    
    addSystemMessage(content) {
      const systemMessage = {
        type: 'agent',
        content,
        timestamp: new Date()
      }
      this.messages.push(systemMessage)
      this.scrollToBottom()
    },
    
    clearChat() {
      this.messages = []
      this.selectedAgent = null
    },
    
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatContainer) {
          this.$refs.chatContainer.scrollTop = this.$refs.chatContainer.scrollHeight
        }
      })
    },
    
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.animate-bounce {
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}
</style> 